// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
/**
 * Manages submenu open/close state, keeps subitems in sync when parent items change,
 * registers menu ref in shared openedMenuRefs, and handles z-order for portal menus.
 */
export function useSubMenuState({ menuRef, items, openedMenuRefs, hasParent, keepRootAboveSubmenu = true }) {
    const [subMenuState, setSubMenuState] = useState(null);
    const openSubMenu = (item, position) => {
        setSubMenuState({ parent: item, position, items: item.subitems });
    };
    const closeSubmenu = () => {
        setSubMenuState(null);
    };
    useEffect(() => {
        if (subMenuState !== null) {
            const item = items.find((newItem) => newItem.id === subMenuState.parent.id);
            if (item !== undefined && isValidArray(item.subitems)) {
                setSubMenuState({ parent: item, position: subMenuState.position, items: item.subitems });
            }
            else {
                closeSubmenu();
            }
        }
    }, [items]);
    // Move root menu behind its first submenu (once).
    // Portal order is [Root, Level2, Level3 …]. After appendChild: [Level2, Root, Level3 …].
    // Root stays above Level2 (overlap zone) but below Level3+.
    // Runs only on the null → non-null transition so re-hovering the same item doesn't push Root past deeper levels.
    const wasSubMenuOpen = useRef(false);
    useLayoutEffect(() => {
        var _a;
        if (!hasParent && keepRootAboveSubmenu) {
            if (subMenuState !== null && !wasSubMenuOpen.current) {
                wasSubMenuOpen.current = true;
                const node = menuRef.current;
                (_a = node === null || node === void 0 ? void 0 : node.parentElement) === null || _a === void 0 ? void 0 : _a.appendChild(node);
            }
            if (subMenuState === null) {
                wasSubMenuOpen.current = false;
            }
        }
    }, [subMenuState, menuRef, hasParent, keepRootAboveSubmenu]);
    // Register this menu's ref so every ancestor Popover sees it in excludeRefs.
    useEffect(() => {
        const refs = openedMenuRefs;
        refs.push(menuRef);
        return () => {
            const idx = refs.indexOf(menuRef);
            if (idx !== -1) {
                refs.splice(idx, 1);
            }
        };
    }, [openedMenuRefs, menuRef]);
    return { subMenuState, openSubMenu, closeSubmenu };
}
